import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, take } from 'rxjs';
import { S3PresignedUrl } from 'src/app/classes/s3-presigned-url';

import { Site, SiteUser, SiteVendor } from '../classes/models';
import { SiteFile } from '../classes/models/site-file';
import { SiteVendorFile } from '../classes/models/site-vendor-file';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class SiteService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService
  ) {}

  getSites(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<Site[]>;
        } = {
          Vendor: () =>
            this.http.get<Site[]>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/site/many`,
              { params: query }
            ),
          Client: () =>
            this.http.get<Site[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<Site[]>(`/api/v2/admin/site/many`, { params: query })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getSite(query = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Vendor: () =>
            this.http.get(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/site/one`,
              { params: query }
            ),
          Client: () =>
            this.http.get(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/one`,
              { params: query }
            ),
          Admin: () =>
            this.http.get(`/api/v2/admin/site/one`, { params: query })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getSiteCount(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Vendor: () =>
            this.http.get<number>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/site/count`,
              { params: query }
            ),
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/count`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<number>(`/api/v2/admin/site/count`, { params: query })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getSiteVendors(query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Vendor: () =>
            this.http.get(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/site/siteVendor/many`,
              { params: query }
            ),
          Client: () =>
            this.http.get(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/siteVendor/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get(`/api/v2/admin/site/siteVendor/many`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getSiteVendorCount(query: {} = {}): Observable<number> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Vendor: () =>
            this.http.get<number>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/site/siteVendor/count`,
              { params: query }
            ),
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/siteVendor/count`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<number>(`/api/v2/admin/site/siteVendor/count`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveSiteVendor(SiteVendor: SiteVendor, query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/siteVendor`,
              SiteVendor,
              { params: query }
            ),
          Admin: () =>
            this.http.post(`/api/v2/admin/site/siteVendor`, SiteVendor, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  deleteSiteVendor(query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.delete(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/siteVendor`,
              { params: query }
            ),
          Admin: () =>
            this.http.delete(`/api/v2/admin/site/siteVendor`, { params: query })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getSiteUsers(query: {} = {}): Observable<SiteUser[]> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<SiteUser[]>;
        } = {
          Vendor: () =>
            this.http.get<SiteUser[]>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/site/siteUser/many`,
              { params: query }
            ),
          Client: () =>
            this.http.get<SiteUser[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/siteUser/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<SiteUser[]>(`/api/v2/admin/site/siteUser/many`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getSiteUserCount(query: {} = {}): Observable<number> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Vendor: () =>
            this.http.get<number>(
              `/api/v2/vendor/${this.AuthService.getUserVendorId()}/site/siteUser/count`,
              { params: query }
            ),
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/siteUser/count`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveSiteUser(SiteUser: SiteUser, query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/siteUser/one`,
              SiteUser,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveSiteUsers(SiteUsers: SiteUser[], query: { UserId: number }) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/siteUser/many`,
              SiteUsers,
              { params: query }
            ),
          Admin: () =>
            this.http.post(`/api/v2/admin/site/siteUser/many`, SiteUsers, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  deleteSiteUser(SiteUserId: number, query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.delete(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/siteUser/${SiteUserId}`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveSite(Site: Site, query = {}): Observable<Site> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post<Site>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site`,
              Site,
              { params: query }
            ),
          Admin: () =>
            this.http.post<Site>(`/api/v2/admin/site`, Site, { params: query })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveSites(Sites: Site[], query = {}): Observable<Site> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post<Site>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/saveSites`,
              Sites,
              { params: query }
            ),
          Admin: () =>
            this.http.post<Site>(`/api/v2/admin/site/saveSites`, Sites, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  deleteSite(query = {}): Observable<number> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.delete<number>(`/api/v2/admin/site/`, { params: query })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  processSitesUpload(SitesFile: File, query = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.post<any>(
              `/api/v2/admin/site/processSitesUpload`,
              SitesFile,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getSitesUploadTemplate(query = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.get(`/api/v2/admin/site/getSitesUploadTemplate`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  checkForDuplicateSite(Site: Site, query = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.post<any>(
              `/api/v2/admin/site/checkForDuplicateSite`,
              Site,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  saveSiteFiles(SiteFiles: SiteFile[], query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<SiteFile[]>;
        } = {
          Client: () =>
            this.http.post<SiteFile[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/siteFile/many`,
              SiteFiles,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getSiteFiles(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<SiteFile[]>;
        } = {
          Client: () =>
            this.http.get<SiteFile[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/siteFile/many`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getSiteFileCount(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Client: () =>
            this.http.get<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/siteFile/count`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  deleteSiteFile(query = {}) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<number>;
        } = {
          Client: () =>
            this.http.delete<number>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/siteFile/one`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getSiteS3PresignedUrl(SiteId: number, FileId: number) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<S3PresignedUrl>;
        } = {
          Client: () =>
            this.http.get<S3PresignedUrl>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/site/siteFile/presignedUrl`,
              { params: { SiteId, FileId } }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getSiteVendorFile(query = {}) {
    return this.http.get<SiteVendorFile>(`/api/v2/siteVendorFile/one`, {
      params: query
    });
  }

  getSiteVendorFiles(query = {}) {
    return this.http.get<SiteVendorFile[]>(`/api/v2/siteVendorFile/many`, {
      params: query
    });
  }

  getSiteVendorFileCount(query = {}) {
    return this.http.get<number>(`/api/v2/siteVendorFile/count`, {
      params: query
    });
  }

  saveSiteVendorFiles(SiteVendorFiles: SiteVendorFile[], query = {}) {
    return this.http.post<SiteVendorFile[]>(
      `/api/v2/siteVendorFile/many`,
      SiteVendorFiles,
      { params: query }
    );
  }

  deleteSiteVendorFile(query = {}) {
    return this.http.delete<number>(`/api/v2/siteVendorFile/one`, {
      params: query
    });
  }

  getSiteVendorFileS3PresignedUrl(SiteVendorId: number, FileId: number) {
    return this.http.get<S3PresignedUrl>(
      `/api/v2/siteVendorFile/presignedUrl`,
      { params: { SiteVendorId, FileId } }
    );
  }
}
